import { z } from 'zod';

/**
 * One schema pair per form: an input schema that drives both react-hook-form
 * validation and the request type, and a response schema that is parsed rather
 * than cast, so a backend contract change fails loudly at the boundary.
 */

/**
 * Country is fixed at registration and decides which country-isolated database
 * the tenant lives in. There are no cross-country queries, so this cannot be
 * changed later without a migration.
 */
export const countrySchema = z.enum(['NG', 'CA']);

export type Country = z.infer<typeof countrySchema>;

export const countryLabels: Record<Country, string> = {
  NG: 'Nigeria',
  CA: 'Canada',
};

export const sessionUserSchema = z.object({
  id: z.uuid(),
  displayName: z.string().min(1),
  // Null for an account registered by phone with no address linked (ADR-0024). This
  // parse rejecting a fabricated "" is what surfaced the contract bug; nullable is
  // the honest shape, not a loosening.
  email: z.email().nullable(),
  businessName: z.string().min(1),
  country: countrySchema,
});

export type SessionUser = z.infer<typeof sessionUserSchema>;

/** Login, registration, and refresh all resolve to this shape. */
export const authSuccessSchema = z.object({
  accessToken: z.string().min(1),
  expiresInSeconds: z.number().int().positive(),
  user: sessionUserSchema,
});

export type AuthSuccess = z.infer<typeof authSuccessSchema>;

export const loginInputSchema = z.object({
  email: z.email('Enter the email address you signed up with'),
  password: z.string().min(1, 'Enter your password'),
});

export type LoginInput = z.infer<typeof loginInputSchema>;

// Keep the minimum in step with the API's ASP.NET Core Identity PasswordOptions.
const PASSWORD_MIN_LENGTH = 12;

/**
 * No country field: registration states Nigeria rather than asking (ADR-0019, matching
 * the phone flow). The mutation supplies the code; a selector offering a second country
 * would be a launch claim the product cannot honour.
 */
export const registerInputSchema = z
  .object({
    fullName: z.string().trim().min(2, 'Enter your full name'),
    businessName: z.string().trim().min(2, 'Enter the name your customers know you by'),
    email: z.email('Enter a valid email address'),
    password: z
      .string()
      .min(PASSWORD_MIN_LENGTH, `Use at least ${String(PASSWORD_MIN_LENGTH)} characters`),
    confirmPassword: z.string(),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: 'Both passwords must match',
    path: ['confirmPassword'],
  });

export type RegisterInput = z.infer<typeof registerInputSchema>;

/**
 * Google only (ADR-0031, superseding ADR-0016's three-provider set). Facebook is
 * dropped and Sign in with Apple waits for the iOS release, where App Store
 * Guideline 4.8 makes it mandatory again; the API no longer registers either scheme,
 * so a button here would be a dead control.
 */
export const oauthProviders = ['google'] as const;

export type OAuthProvider = (typeof oauthProviders)[number];

export const oauthProviderLabels: Record<OAuthProvider, string> = {
  google: 'Google',
};

/**
 * Phone registration and sign-in (ADR-0024).
 *
 * The client validates loosely and the API canonicalises: a Nigerian trader types
 * 0803..., 803..., 234803... or +234803..., all of which are the same person, and
 * deciding which is which is the server's job because the unique index that makes
 * the answer enforceable lives there. This rule only catches input that could not
 * be any of those, so the form fails fast without duplicating a rule that must not
 * drift.
 */
const NIGERIAN_MOBILE = /^(\+?234|0)?[789]\d{9}$/;

export const phoneStartInputSchema = z.object({
  phoneNumber: z
    .string()
    .trim()
    .transform((value) => value.replaceAll(/[\s()-]/g, ''))
    .pipe(z.string().regex(NIGERIAN_MOBILE, 'Enter a Nigerian mobile number, like 0803 123 4567')),
});

export type PhoneStartInput = z.infer<typeof phoneStartInputSchema>;

export const phoneStartResponseSchema = z.object({
  verificationId: z.uuid(),
  expiresInSeconds: z.number().int().positive(),
});

export type PhoneStartResponse = z.infer<typeof phoneStartResponseSchema>;

export const phoneCodeInputSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'The code is 6 digits'),
});

export type PhoneCodeInput = z.infer<typeof phoneCodeInputSchema>;

/**
 * A known number returns a session; an unknown one returns a completion token. Both
 * are ordinary outcomes of the same call, which is what stops this flow revealing
 * whether a number already has an account.
 */
export const phoneVerifyResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('signed_in'),
    session: authSuccessSchema,
  }),
  z.object({
    status: z.literal('registration_required'),
    registration: z.object({
      completionToken: z.string().min(1),
      phoneNumber: z.string().min(1),
    }),
  }),
]);

export type PhoneVerifyResponse = z.infer<typeof phoneVerifyResponseSchema>;

/** The third screen asks for the business name alone (ADR-0024). */
export const phoneRegisterInputSchema = z.object({
  businessName: z.string().trim().min(2, 'Enter the name your customers know you by'),
});

export type PhoneRegisterInput = z.infer<typeof phoneRegisterInputSchema>;
